/* eslint-disable ember/no-computed-properties-in-native-classes */
import Model, {
  belongsTo,
  attr,
  type AsyncBelongsTo,
} from '@warp-drive/legacy/model';
import { computed } from '@ember/object';
import { alias } from '@ember/object/computed';
import { isEmpty } from '@ember/utils';
import type { Type } from '@warp-drive/core/types/symbols';
import type Event from '@linc-technologies/linced-common/models/events/event';
import moment from 'moment';
import NswSapSyncStatus from 'linced-app/enums/nsw-sap-sync-status';
import type CostCentre from 'linced-app/models/finance/cost-centre';
import type InternalOrder from 'linced-app/models/finance/internal-order';
import type Ledger from 'linced-app/models/finance/ledger';
import type School from 'linced-app/models/school';

// Proto: finance.LedgerItem.
export default class LedgerItem extends Model {
  declare [Type]: 'finance/ledger-item';

  @belongsTo('school', { async: true, inverse: null })
  declare schoolId: AsyncBelongsTo<School>;

  @alias('schoolId')
  declare school: AsyncBelongsTo<School>;

  @attr('string', { defaultValue: '' })
  declare campusId: string; // TODO: implement when we have Campuses

  @attr('number')
  declare updateTime: number | undefined;

  @attr('number')
  declare createTime: number | undefined;

  @belongsTo('finance/ledger', { async: true, inverse: null })
  declare ledgerId: AsyncBelongsTo<Ledger>;

  @alias('ledgerId')
  declare ledger: AsyncBelongsTo<Ledger>;

  @attr('string', { defaultValue: '' })
  declare name: string;

  @attr('string', { defaultValue: '' })
  declare defaultDescription: string;

  @attr('string')
  declare defaultDate: string | undefined;

  @attr('number', { defaultValue: 0 })
  declare defaultAmount: number;

  @attr('boolean', { defaultValue: false })
  declare isTaxable: boolean;

  @attr('boolean', { defaultValue: false })
  declare isDonation: boolean;

  @attr('number', { defaultValue: 0 })
  declare taxType: number;

  // numLineItems is a virtual attribute, calculated by the back end.
  @attr('number', { defaultValue: 0 })
  declare numLineItems: number;

  @computed('defaultDate', 'name')
  get prefixedName() {
    const thisYear = moment().year();
    const year = moment(this.defaultDate).year();
    let yearPrefix = '';
    if (year !== thisYear) {
      yearPrefix = `[${year}] `;
    }
    return `${yearPrefix}${this.name}`;
  }

  @computed('defaultDescription', 'prefixedName')
  get lineItemSelectOptions() {
    if (
      !isEmpty(this.defaultDescription) &&
      !isEmpty(this.defaultDescription.trim())
    ) {
      return `${this.prefixedName} - ${this.defaultDescription}`;
    } else {
      return this.prefixedName;
    }
  }

  // Extra properties for NSW.
  // isRefundable flags whether Line Items based off this Ledger Item are
  // able to be refunded.
  @attr('boolean', { defaultValue: false })
  declare isRefundable: boolean;

  // costCentreId is the unique ID of the linked Cost Centre, if any.
  @belongsTo('finance/cost-centre', { async: true, inverse: null })
  declare costCentreId: AsyncBelongsTo<CostCentre>;

  @alias('costCentreId')
  declare costCentre: AsyncBelongsTo<CostCentre>;

  // internalOrderId is the unique ID of the linked Internal Order, if any.
  @belongsTo('finance/internal-order', { async: true, inverse: null })
  declare internalOrderId: AsyncBelongsTo<InternalOrder>;

  @alias('internalOrderId')
  declare internalOrder: AsyncBelongsTo<InternalOrder>;

  // reference is used to hold the Fee Value ID for SAP integration.
  @attr('string', { defaultValue: '' })
  declare reference: string;

  // referenceOrId returns the reference, falling back to id.
  @computed('reference', 'id')
  get referenceOrId() {
    if (isEmpty(this.reference)) {
      return this.id;
    }
    return this.reference;
  }

  // nswSapStatus is the SAP sync status for this Ledger Item, if applicable.
  @attr('number', { defaultValue: 0 })
  declare nswSapStatus: number;

  // nswSapMessage is the SAP sync message for this Ledger Item, if applicable.
  @attr('string', { defaultValue: '' })
  declare nswSapMessage: string;

  // nswSapOk determines whether the SAP sync status is such that Line Items
  // can be created from this Ledger Item.
  @computed('nswSapStatus', 'reference')
  get nswSapOk() {
    if (isEmpty(this.reference)) {
      return false;
    }
    // 0 is not synced.
    return (
      Boolean(this.nswSapStatus) &&
      this.nswSapStatus !== NswSapSyncStatus.FAILED
    );
  }

  @belongsTo('events/event', { async: true, inverse: null })
  declare eventId: AsyncBelongsTo<Event>;

  @alias('eventId')
  declare event: AsyncBelongsTo<Event>;

  @alias('event.id')
  declare event_id: string | null | undefined;
}
